import { useContext } from "react";
import { Box, Typography } from "@mui/material";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Appointment } from "../../../state/appointments";
import { typography } from "../../../utils/typography";
import { semanticColors } from "../../../utils/colors";
import AppointmentsTable, {
  AppointmentColumn,
  TextCell,
  PatientNameCell,
  StatusCell,
  PictureMapContext
} from "./AppointmentsTable";

const DEFAULT_LENGTH = 30;

const containerSx = { height: "100%", overflowY: "auto" };

const noEventsSx = {
  ...typography.body.l,
  color: semanticColors.text.secondary,
  mt: 4,
  textAlign: "center"
};

/**
 * Custom react-big-calendar "Agenda" view rendered with the shared appointments
 * table (date | start time | patient | duration | status). The static
 * range/navigate/title methods mirror the built-in Agenda so the toolbar
 * keeps working.
 */
const AgendaTableView: any = ({
  events,
  date,
  length = DEFAULT_LENGTH
}: any) => {
  const { t } = useTranslation();
  const pictureMap = useContext(PictureMapContext);

  const rangeStart = moment(date).startOf("day");
  const rangeEnd = moment(date)
    .add(length, "days")
    .endOf("day");

  const rows: Appointment[] = (events as Appointment[])
    .filter(e =>
      moment(e.start).isBetween(rangeStart, rangeEnd, undefined, "[]")
    )
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const columns: AppointmentColumn[] = [
    {
      key: "patient",
      label: t("videoConsultations.tablePatient"),
      render: appointment => (
        <PatientNameCell
          appt={appointment}
          pictureUrl={pictureMap[appointment.patientName.toLowerCase()]}
        />
      )
    },
    {
      key: "date",
      label: t("videoConsultations.tableDate"),
      width: "140px",
      render: appointment => (
        <TextCell>{moment(appointment.start).format("DD/MM/YYYY")}</TextCell>
      )
    },
    {
      key: "time",
      label: t("videoConsultations.tableStartTime"),
      width: "130px",
      render: appointment => (
        <TextCell>{moment(appointment.start).format("HH:mm")}</TextCell>
      )
    },
    {
      key: "duration",
      label: t("videoConsultations.tableDuration"),
      width: "110px",
      render: appointment => (
        <TextCell>{appointment.durationMinutes} min</TextCell>
      )
    },
    {
      key: "status",
      label: t("videoConsultations.tableStatus"),
      width: "120px",
      render: appointment => <StatusCell appt={appointment} />
    }
  ];

  return (
    <Box sx={containerSx}>
      {rows.length === 0 ? (
        <Typography sx={noEventsSx}>{t("calendar.noEventsInRange")}</Typography>
      ) : (
        <AppointmentsTable columns={columns} rows={rows} />
      )}
    </Box>
  );
};

AgendaTableView.range = (
  start: Date,
  { length = DEFAULT_LENGTH, localizer }: any
) => ({
  start,
  end: localizer.add(start, length, "day")
});

AgendaTableView.navigate = (
  date: Date,
  action: string,
  { length = DEFAULT_LENGTH, localizer }: any
) => {
  if (action === "PREV") return localizer.add(date, -length, "day");
  if (action === "NEXT") return localizer.add(date, length, "day");
  return date;
};

AgendaTableView.title = (
  start: Date,
  { length = DEFAULT_LENGTH, localizer }: any
) =>
  localizer.format(
    { start, end: localizer.add(start, length, "day") },
    "agendaHeaderFormat"
  );

export default AgendaTableView;
